import * as React from 'react';
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Tooltip,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import { useTranslate, useGetIdentity } from 'react-admin';

import { useResolvedActiveAppId } from '../apps/useResolvedActiveAppId';
import {
    ACCOUNTING_SUITE_APP_ID,
    AUTO_DEALERS_APP_ID,
    AUTO_WORKSHOP_APP_ID,
    FUELING_STATIONS_APP_ID,
    INVENTORY_APP_ID,
    PRA_WORKSPACE_APP_ID,
    PROJECT_MANAGEMENT_APP_ID,
    DOCUMENT_MANAGEMENT_APP_ID,
    PAYROLL_APP_ID,
    PURCHASES_APP_ID,
    SETTINGS_APP_ID,
} from '../apps/appsRegistry';
import {
    INVENTORY_CONFIGURATION_NAV,
    INVENTORY_MAINTENANCE_NAV,
    INVENTORY_REPORTS_NAV,
    INVENTORY_TRANSACTIONS_NAV_SECTIONS,
} from '../inventory/inventoryTransactionsNav';
import { FUEL_STATION_REPORTS_NAV_SECTIONS } from '../fuelingStation/reports/fuelStationReportsNav';
import { WORKSHOP_PAYMENTS_NAV } from '../workshop/workshopPaymentsNav';
import { WORKSHOP_REPORTS_NAV } from '../workshop/workshopReportsNav';
import { WORKSHOP_SALES_NAV } from '../workshop/workshopSalesNav';
import { WORKSHOP_SERVICES_NAV } from '../workshop/workshopServicesNav';
import { WORKSHOP_SETUP_NAV } from '../workshop/workshopSetupNav';
import type { WorkshopNavItem } from '../workshop/workshopNavTypes';
import { PM_PURCHASE_REGISTER_NAV, PROJECT_REPORTS_NAV } from '../projects/reports/projectReportsNav';
import { useTenantApps } from '../apps/tenantApps/useTenantApps';
import { PURCHASE_REPORTS_NAV } from '../purchases/reports/purchaseReportsNav';
import { PAYROLL_REPORTS_NAV } from '../hr/reports/payrollReportsNav';
import { SETTINGS_SECURITY_GROUPS_LIST_PATH, SETTINGS_USERS_LIST_PATH } from '../apps/workspacePaths';
import { useAccountingAccess } from '../accounting/useAccountingAccess';
import { useCanAccess, useCanAccessAnyInNavSection } from '../auth/useCanAccess';
import { pathInWorkspace, workspaceRootPath } from './odooNavUtils';

const DRAWER_WIDTH = 288;

/**
 * Below `lg`, the horizontal Odoo module bar is hidden; a hamburger opens this drawer
 * (same destinations as the desktop top nav).
 */
export function OdooShellMobileNav() {
    const [open, setOpen] = React.useState(false);
    const close = () => setOpen(false);
    const location = useLocation();
    const translate = useTranslate();
    const activeAppId = useResolvedActiveAppId();
    const { identity } = useGetIdentity();
    const companyActive =
        (identity as { companyIsActivated?: boolean } | undefined)?.companyIsActivated !== false &&
        (identity as { companyLicenseLocked?: boolean } | undefined)?.companyLicenseLocked !== true;
    const root = workspaceRootPath(activeAppId);
    const p = (path: string) => pathInWorkspace(root, path);
    const canReadCoa = useAccountingAccess('glChartAccounts', 'read');
    const canJv = useAccountingAccess('glJournalVouchers', 'read');
    const canBills = useAccountingAccess('vendorBills', 'read');
    const canInvoices = useAccountingAccess('customerInvoices', 'read');
    const canVoucherTypes = useAccountingAccess('glVoucherTypes', 'read');
    const canAccountGroups = useAccountingAccess('glAccountGroups', 'read');
    const canGenBank = useAccountingAccess('genBankInformation', 'read');
    const canGenCash = useAccountingAccess('genCashInformation', 'read');
    const canReadVendors = useAccountingAccess('vendors', 'read');
    const canTrialBalance  = useAccountingAccess('trialBalance',  'read');
    const canGeneralLedger = useAccountingAccess('generalLedger', 'read');
    const canPartiesLedger = useAccountingAccess('partiesLedger', 'read');
    const canTaxLedger     = useAccountingAccess('taxLedger',     'read');
    const canProfitLoss    = useAccountingAccess('profitLoss',    'read');
    const canReports = canTrialBalance || canGeneralLedger || canPartiesLedger || canTaxLedger || canProfitLoss;
    const canSecurityGroups = useCanAccess(SETTINGS_APP_ID, 'securityGroups', 'read');
    const canUsers = useCanAccess(SETTINGS_APP_ID, 'users', 'read');
    const canDocSeq = useCanAccess(SETTINGS_APP_ID, 'documentSequences', 'read');
    const canPurchasesPo = useCanAccess(PURCHASES_APP_ID, 'purchaseOrders', 'read');
    const canPurchasesRfq = useCanAccess(PURCHASES_APP_ID, 'purchaseRequisitions', 'read');
    const canPurchasesGrn = useCanAccess(PURCHASES_APP_ID, 'inwardGatePasses', 'read');
    const canPurchasesPi = useCanAccess(PURCHASES_APP_ID, 'purchaseInvoices', 'read');
    const canPurchaseReturns = useCanAccess(PURCHASES_APP_ID, 'purchaseReturns', 'read');
    const canPurchaseRegister = useCanAccess(PURCHASES_APP_ID, 'purchaseRegisterReport', 'read');
    // PM's Reporting menu offers the Purchases-owned register only when the Purchases app
    // is actually installed for the tenant (cross-app mount, same rule as the desktop bar).
    const { isAppInstalled } = useTenantApps();
    const purchasesInstalled = isAppInstalled(PURCHASES_APP_ID);
    const canInventoryWarehouses = useCanAccess(INVENTORY_APP_ID, 'inventWarehouses', 'read');
    const canStockPatch = useCanAccess(INVENTORY_APP_ID, 'stockPatch', 'read');
    const canAutoDealerSales = useCanAccessAnyInNavSection(AUTO_DEALERS_APP_ID, 'sales');
    const canAutoDealerSetup = useCanAccessAnyInNavSection(AUTO_DEALERS_APP_ID, 'setup');
    const canAutoDealerReports = useCanAccessAnyInNavSection(AUTO_DEALERS_APP_ID, 'reports');
    const canFuelStations = useCanAccess(FUELING_STATIONS_APP_ID, 'fuelStations', 'read');
    const canFuelEntries = useCanAccess(FUELING_STATIONS_APP_ID, 'fuelStationDailyEntries', 'read');
    const canWorkshopServices = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'services');
    const canWorkshopSales = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'sales');
    const canWorkshopPayments = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'paymentsRecoveries');
    const canWorkshopSetup = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'setup');
    const canWorkshopReports = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'reports');
    const canInventoryTransactions = useCanAccessAnyInNavSection(INVENTORY_APP_ID, 'transactions');
    const canInventoryReports = useCanAccess(INVENTORY_APP_ID, 'itemStockReport', 'read');
    const canPmProjects = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectInfo', 'read');
    const canPmTasks = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectTask', 'read');
    const canPmWbs = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectWbs', 'read');
    const canPmBudget = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectBudget', 'read');
    const canPmSchedules = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectPaymentSchedule', 'read');
    const canPmChangeRequests = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectChangeRequest', 'read');
    const canPmCategories = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectCategory', 'read');
    const canPmLaborTypes = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectLaborType', 'read');
    const canPmRoles = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectRole', 'read');
    const canDmsDocuments = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'documentInfo', 'read');
    const canDmsCategories = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'documentCategory', 'read');
    const canDmsTypes = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'documentType', 'read');
    const canDmsAccessLog = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'accessLog', 'read');
    const canDmsMigration = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'legacyMigration', 'read');
    // Employees is owned by the Auto Workshop app; PM just links to the shared screens.
    const canPmEmployees = useCanAccess(AUTO_WORKSHOP_APP_ID, 'employee', 'read');
    const canHrPayslips = useCanAccess(PAYROLL_APP_ID, 'payslips', 'read');
    const canHrPeriods = useCanAccess(PAYROLL_APP_ID, 'payrollPeriods', 'read');
    const canHrLoans = useCanAccess(PAYROLL_APP_ID, 'loans', 'read');
    const canHrComponents = useCanAccess(PAYROLL_APP_ID, 'salaryComponents', 'read');
    const canHrStructures = useCanAccess(PAYROLL_APP_ID, 'salaryStructures', 'read');
    const canHrTaxSlabs = useCanAccess(PAYROLL_APP_ID, 'taxSlabs', 'read');
    const canHrSalarySheet = useCanAccess(PAYROLL_APP_ID, 'salarySheetReport', 'read');

    React.useEffect(() => {
        setOpen(false);
    }, [location.pathname, location.search]);

    const itemSx = { borderRadius: 1, mx: 0.5 } as const;

    let body: React.ReactNode;
    if (!companyActive) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
            </List>
        );
    } else if (activeAppId === ACCOUNTING_SUITE_APP_ID) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('shell.accounting.dashboard')} />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/customers')} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('shell.accounting.customers')} />
                </ListItemButton>
                {canBills ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{
                                fontWeight: 700,
                                fontSize: 12,
                                lineHeight: '32px',
                                color: 'primary.main',
                            }}
                        >
                            {translate('shell.accounting.transaction_vendor_bills', { _: 'Vendor Bills' })}
                        </ListSubheader>
                        <ListItemButton component={Link} to={p('/vendorBills')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('shell.accounting.bills', { _: 'Bills' })} />
                        </ListItemButton>
                    </>
                ) : null}
                {canInvoices ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{
                                fontWeight: 700,
                                fontSize: 12,
                                lineHeight: '32px',
                                color: 'primary.main',
                            }}
                        >
                            {translate('shell.accounting.transaction_customer_invoices', { _: 'Customer Invoices' })}
                        </ListSubheader>
                        <ListItemButton component={Link} to={p('/customerInvoices')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('shell.accounting.invoices', { _: 'Invoices' })} />
                        </ListItemButton>
                    </>
                ) : null}
                {canJv ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{
                                fontWeight: 700,
                                fontSize: 12,
                                lineHeight: '32px',
                                color: 'primary.main',
                            }}
                        >
                            {translate('shell.accounting.transaction')}
                        </ListSubheader>
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 600, fontSize: 11, pl: 2, lineHeight: '28px' }}
                        >
                            {translate('shell.accounting.transaction_payments')}
                        </ListSubheader>
                        <ListItemButton component={Link} to={p('/bankPayments')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('shell.accounting.bank_payments')} />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/cashPayments')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('shell.accounting.cash_payments')} />
                        </ListItemButton>
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 600, fontSize: 11, pl: 2, lineHeight: '28px' }}
                        >
                            {translate('shell.accounting.transaction_receipts')}
                        </ListSubheader>
                        <ListItemButton component={Link} to={p('/bankReceipts')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('shell.accounting.bank_receipts')} />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/cashReceipts')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('shell.accounting.cash_receipts')} />
                        </ListItemButton>
                    </>
                ) : null}
                <ListSubheader disableSticky sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px' }}>
                    {translate('shell.accounting.accounting')}
                </ListSubheader>
                <ListItemButton component={Link} to={p('/glJournalVouchers/create')} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('shell.accounting.journal_vouchers')} />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/glJournalVouchers')} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('shell.accounting.vouchers_log_book')} />
                </ListItemButton>
                {canReports ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                        >
                            {translate('shell.accounting.reporting')}
                        </ListSubheader>
                        {(canTrialBalance || canGeneralLedger || canPartiesLedger || canTaxLedger) && (
                            <ListSubheader disableSticky sx={{ fontWeight: 600, fontSize: 11, pl: 2, lineHeight: '28px' }}>
                                {translate('shell.accounting.ledgers_section')}
                            </ListSubheader>
                        )}
                        {canTrialBalance && (
                            <ListItemButton component={Link} to={p('/reports/trial-balance')} onClick={close} sx={itemSx}>
                                <ListItemText primary={translate('shell.accounting.trial_balance')} />
                            </ListItemButton>
                        )}
                        {canGeneralLedger && (
                            <ListItemButton component={Link} to={p('/reports/general-ledger')} onClick={close} sx={itemSx}>
                                <ListItemText primary={translate('shell.accounting.general_ledger')} />
                            </ListItemButton>
                        )}
                        {canPartiesLedger && (
                            <ListItemButton component={Link} to={p('/reports/parties-ledger')} onClick={close} sx={itemSx}>
                                <ListItemText primary={translate('shell.accounting.parties_ledger', { _: 'Parties Ledger' })} />
                            </ListItemButton>
                        )}
                        {canTaxLedger && (
                            <ListItemButton component={Link} to={p('/reports/tax-ledger')} onClick={close} sx={itemSx}>
                                <ListItemText primary={translate('shell.accounting.tax_ledger', { _: 'Tax Ledger' })} />
                            </ListItemButton>
                        )}
                        {canProfitLoss && (
                            <>
                                <ListSubheader disableSticky sx={{ fontWeight: 600, fontSize: 11, pl: 2, lineHeight: '28px' }}>
                                    Financial Reports
                                </ListSubheader>
                                <ListItemButton component={Link} to={p('/reports/profit-loss')} onClick={close} sx={itemSx}>
                                    <ListItemText primary="Profit &amp; Loss" />
                                </ListItemButton>
                            </>
                        )}
                    </>
                ) : null}
                <Divider sx={{ my: 1 }} />
                <ListSubheader
                    disableSticky
                    sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                >
                    {translate('shell.accounting.configuration')}
                </ListSubheader>
                {canReadCoa ? (
                    <ListItemButton component={Link} to={p('/glChartAccounts')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('shell.accounting.chart_of_accounts')} />
                    </ListItemButton>
                ) : (
                    <Tooltip title={translate('shell.accounting.no_access_chart')}>
                        <span>
                            <ListItemButton disabled sx={itemSx}>
                                <ListItemText primary={translate('shell.accounting.chart_of_accounts')} />
                            </ListItemButton>
                        </span>
                    </Tooltip>
                )}
                {canVoucherTypes ? (
                    <ListItemButton component={Link} to={p('/glVoucherTypes')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('shell.accounting.voucher_types')} />
                    </ListItemButton>
                ) : null}
                {canAccountGroups ? (
                    <ListItemButton component={Link} to={p('/glAccountGroups')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('shell.accounting.account_groups', { _: 'Account groups' })} />
                    </ListItemButton>
                ) : null}
                {canGenBank ? (
                    <ListItemButton component={Link} to={p('/genBankInformation')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('shell.accounting.bank_information')} />
                    </ListItemButton>
                ) : null}
                {canGenCash ? (
                    <ListItemButton component={Link} to={p('/genCashInformation')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('shell.accounting.cash_information')} />
                    </ListItemButton>
                ) : null}
                {canReadVendors ? (
                    <ListItemButton component={Link} to={p('/vendors')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('shell.accounting.vendors')} />
                    </ListItemButton>
                ) : null}
            </List>
        );
    } else if (activeAppId === SETTINGS_APP_ID) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                {canUsers ? (
                    <ListItemButton component={Link} to={SETTINGS_USERS_LIST_PATH} onClick={close} sx={itemSx}>
                        <ListItemText
                            primary={translate('resources.users.name', {
                                smart_count: 2,
                                _: 'Users',
                            })}
                        />
                    </ListItemButton>
                ) : null}
                {canSecurityGroups ? (
                    <ListItemButton
                        component={Link}
                        to={SETTINGS_SECURITY_GROUPS_LIST_PATH}
                        onClick={close}
                        sx={itemSx}
                    >
                        <ListItemText
                            primary={translate('shell.settings.security_groups', { _: 'Security groups' })}
                        />
                    </ListItemButton>
                ) : null}
                {canDocSeq ? (
                    <ListItemButton component={Link} to={p('/documentSequences')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('shell.settings.document_sequences', { _: 'Document sequences' })} />
                    </ListItemButton>
                ) : null}
            </List>
        );
    } else if (activeAppId === PURCHASES_APP_ID) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                {canPurchasesRfq || canPurchasesPo || canPurchasesGrn || canPurchasesPi ? (
                    <>
                        <ListSubheader disableSticky sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px' }}>
                            {translate('shell.purchases.orders', { _: 'Orders' })}
                        </ListSubheader>
                        {canPurchasesRfq ? (
                            <ListItemButton component={Link} to={p('/purchaseRequisitions')} onClick={close} sx={itemSx}>
                                <ListItemText primary={translate('shell.purchases.rfq', { _: 'RFQ' })} />
                            </ListItemButton>
                        ) : null}
                        {canPurchasesPo ? (
                            <ListItemButton component={Link} to={p('/purchaseOrders')} onClick={close} sx={itemSx}>
                                <ListItemText
                                    primary={translate('shell.purchases.orders', { _: 'Orders' })}
                                />
                            </ListItemButton>
                        ) : null}
                        {canPurchasesGrn ? (
                            <ListItemButton component={Link} to={p('/inwardGatePasses')} onClick={close} sx={itemSx}>
                                <ListItemText primary={translate('shell.purchases.grn', { _: 'GRN / Inward Gate Pass' })} />
                            </ListItemButton>
                        ) : null}
                        {canPurchasesPi ? (
                            <ListItemButton component={Link} to={p('/purchaseInvoices')} onClick={close} sx={itemSx}>
                                <ListItemText
                                    primary={translate('shell.purchases.vendor_bills', { _: 'Vendor bills' })}
                                />
                            </ListItemButton>
                        ) : null}
                        {canPurchaseReturns ? (
                            <ListItemButton component={Link} to={p('/purchaseReturns')} onClick={close} sx={itemSx}>
                                <ListItemText
                                    primary={translate('shell.purchases.purchase_returns', { _: 'Purchase Returns' })}
                                />
                            </ListItemButton>
                        ) : null}
                    </>
                ) : null}
                {/* Each report has its own permission key — same gate as the desktop bar. */}
                {canPurchaseRegister ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                        >
                            {translate('shell.purchases.reporting', { _: 'Reporting' })}
                        </ListSubheader>
                        {PURCHASE_REPORTS_NAV.map(({ resource, label }) => (
                            <ListItemButton
                                key={resource}
                                component={Link}
                                to={p(`/${resource}`)}
                                onClick={close}
                                sx={itemSx}
                            >
                                <ListItemText primary={label} />
                            </ListItemButton>
                        ))}
                    </>
                ) : null}
            </List>
        );
    } else if (activeAppId === AUTO_DEALERS_APP_ID) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>

                {canAutoDealerSetup ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                        >
                            {translate('shell.auto_dealers.setup', { _: 'Setup' })}
                        </ListSubheader>
                        <ListItemButton component={Link} to={p('/colorInformation')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Color Information" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/vehicleGroupInfo')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Vehicle Group Info" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/variantInformation')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Variant Info" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/salesServiceInfo')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Sales Service" />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to={p('/vehicleSaleGlCompanyDefaults')}
                            onClick={close}
                            sx={itemSx}
                        >
                            <ListItemText
                                primary={translate('shell.auto_dealers.vehicle_sale_gl_company_defaults', {
                                    _: 'Vehicle sale GL (company)',
                                })}
                            />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/genBankInformation')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('shell.accounting.bank_information', { _: 'Bank information' })} />
                        </ListItemButton>
                    </>
                ) : null}

                {canAutoDealerSales ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader disableSticky sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px' }}>
                            {translate('shell.auto_dealers.sales', { _: 'Sales' })}
                        </ListSubheader>
                        <ListItemButton component={Link} to={p('/vehicleCommitmentInfo')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Vehicle Commitment" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/vehicleSaleInfo')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Vehicle Sale" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/vehicleRecoveryInfo')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Vehicle Recovery" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/dispatchInfo')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Dispatch" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/vehicleReceivedInfo')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Vehicle Receive" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/vehicleTransferOtherOutlet')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Vehicle Transfer" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/customers')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('resources.customers.name', { smart_count: 2, _: 'Customers' })} />
                        </ListItemButton>
                    </>
                ) : null}

                {canAutoDealerReports ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                        >
                            {translate('shell.auto_dealers.reports', { _: 'Reports' })}
                        </ListSubheader>
                        <ListItemButton component={Link} to={p('/dispatchReport')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Dispatch report" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/dispatchAging')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Dispatch aging" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/insuranceReport')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Insurance collection" />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/saleAging')} onClick={close} sx={itemSx}>
                            <ListItemText primary="Sale aging" />
                        </ListItemButton>
                    </>
                ) : null}
            </List>
        );
    } else if (activeAppId === FUELING_STATIONS_APP_ID) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                {canFuelStations ? (
                    <ListItemButton component={Link} to={p('/fuelStations')} onClick={close} sx={itemSx}>
                        <ListItemText primary={translate('fuel.stations', { _: 'Fuel Stations' })} />
                    </ListItemButton>
                ) : null}
                {canFuelEntries ? (
                    <>
                        <ListItemButton component={Link} to={p('/daily-entry')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('fuel.daily_entry', { _: 'Daily Entry' })} />
                        </ListItemButton>
                        <ListItemButton component={Link} to={p('/fuelStationDailyEntries')} onClick={close} sx={itemSx}>
                            <ListItemText primary={translate('fuel.transactions_log', { _: 'Transcations' })} />
                        </ListItemButton>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{
                                fontWeight: 700,
                                fontSize: 12,
                                lineHeight: '32px',
                                color: 'primary.main',
                            }}
                        >
                            {translate('shell.fueling_station.reports', { _: 'Reports' })}
                        </ListSubheader>
                        {FUEL_STATION_REPORTS_NAV_SECTIONS.flatMap((section, idx) => [
                            <ListSubheader
                                key={`fuel-rpt-h:${section.sectionLabel}`}
                                disableSticky
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 11,
                                    pl: 2,
                                    lineHeight: '28px',
                                    mt: idx === 0 ? 0 : 0.5,
                                }}
                            >
                                {section.sectionLabel}
                            </ListSubheader>,
                            ...section.items.map(({ resource, label }) => (
                                <ListItemButton
                                    key={`fuel-rpt:${section.sectionLabel}:${resource}`}
                                    component={Link}
                                    to={p(`/${resource}`)}
                                    onClick={close}
                                    sx={itemSx}
                                >
                                    <ListItemText primary={label} />
                                </ListItemButton>
                            )),
                        ])}
                    </>
                ) : null}
            </List>
        );
    } else if (activeAppId === INVENTORY_APP_ID) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                {canInventoryTransactions
                    ? INVENTORY_TRANSACTIONS_NAV_SECTIONS.flatMap(section => [
                          <Divider key={`d:${section.sectionLabel}`} sx={{ my: 1 }} />,
                          <ListSubheader
                              key={`h:${section.sectionLabel}`}
                              disableSticky
                              sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                          >
                              {section.sectionLabel}
                          </ListSubheader>,
                          ...section.items.map(({ resource, label }) => (
                              <ListItemButton
                                  key={resource}
                                  component={Link}
                                  to={p(`/${resource}`)}
                                  onClick={close}
                                  sx={itemSx}
                              >
                                  <ListItemText primary={label} />
                              </ListItemButton>
                          )),
                      ])
                    : null}
                {canInventoryReports ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                        >
                            {translate('inventory.menu.reports', { _: 'Reports' })}
                        </ListSubheader>
                        {INVENTORY_REPORTS_NAV.map(({ resource, label }) => (
                            <ListItemButton
                                key={resource}
                                component={Link}
                                to={p(`/${resource}`)}
                                onClick={close}
                                sx={itemSx}
                            >
                                <ListItemText primary={label} />
                            </ListItemButton>
                        ))}
                    </>
                ) : null}
                {canInventoryWarehouses ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                        >
                            {translate('inventory.menu.configuration', { _: 'Configuration' })}
                        </ListSubheader>
                        {INVENTORY_CONFIGURATION_NAV.map(({ resource, label }) => (
                            <ListItemButton
                                key={resource}
                                component={Link}
                                to={p(`/${resource}`)}
                                onClick={close}
                                sx={itemSx}
                            >
                                <ListItemText primary={label} />
                            </ListItemButton>
                        ))}
                    </>
                ) : null}
                {canStockPatch ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListSubheader
                            disableSticky
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                        >
                            {translate('inventory.menu.maintenance', { _: 'Maintenance' })}
                        </ListSubheader>
                        {INVENTORY_MAINTENANCE_NAV.map(({ resource, label }) => (
                            <ListItemButton
                                key={resource}
                                component={Link}
                                to={p(`/${resource}`)}
                                onClick={close}
                                sx={itemSx}
                            >
                                <ListItemText primary={label} />
                            </ListItemButton>
                        ))}
                    </>
                ) : null}
            </List>
        );
    } else if (activeAppId === AUTO_WORKSHOP_APP_ID) {
        const workshopSection = (
            sectionLabel: string,
            items: WorkshopNavItem[],
            show: boolean
        ) =>
            show ? (
                <>
                    <Divider sx={{ my: 1 }} />
                    <ListSubheader
                        disableSticky
                        sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                    >
                        {sectionLabel}
                    </ListSubheader>
                    {items.map(({ resource, label }) => (
                        <ListItemButton
                            key={resource}
                            component={Link}
                            to={p(`/${resource}`)}
                            onClick={close}
                            sx={itemSx}
                        >
                            <ListItemText primary={label} />
                        </ListItemButton>
                    ))}
                </>
            ) : null;

        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                {workshopSection(
                    translate('shell.auto_workshop.services', { _: 'Services' }),
                    WORKSHOP_SERVICES_NAV,
                    canWorkshopServices
                )}
                {workshopSection(
                    translate('shell.auto_workshop.sales', { _: 'Sales' }),
                    WORKSHOP_SALES_NAV,
                    canWorkshopSales
                )}
                {workshopSection(
                    translate('shell.auto_workshop.payments_recoveries', {
                        _: 'Payments & Recoveries',
                    }),
                    WORKSHOP_PAYMENTS_NAV,
                    canWorkshopPayments
                )}
                {workshopSection(
                    translate('shell.auto_workshop.setup', { _: 'Setup' }),
                    WORKSHOP_SETUP_NAV,
                    canWorkshopSetup
                )}
                {workshopSection(
                    translate('shell.auto_workshop.reports', { _: 'Reports' }),
                    WORKSHOP_REPORTS_NAV,
                    canWorkshopReports
                )}
            </List>
        );
    } else if (activeAppId === PRA_WORKSPACE_APP_ID) {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                <ListSubheader disableSticky sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px' }}>
                    {translate('pos.menu.sales', { _: 'Sales' })}
                </ListSubheader>
                <ListItemButton component={Link} to={p('/praInvoices')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.praInvoices.name', {
                            smart_count: 2,
                            _: 'PRA Invoices',
                        })}
                    />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/customers')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.customers.name', {
                            smart_count: 2,
                            _: 'Customers',
                        })}
                    />
                </ListItemButton>
                <ListSubheader disableSticky sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px' }}>
                    {translate('pos.menu.catalog', { _: 'Catalog' })}
                </ListSubheader>
                <ListItemButton component={Link} to={p('/productProfiles')} onClick={close} sx={itemSx}>
                    <ListItemText primary="Products" />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/fbrSalesTaxRates')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.fbrSalesTaxRates.name', {
                            smart_count: 2,
                            _: 'Taxes',
                        })}
                    />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/companies')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.companies.name', {
                            smart_count: 1,
                            _: 'Company',
                        })}
                    />
                </ListItemButton>
            </List>
        );
    } else if (activeAppId === PROJECT_MANAGEMENT_APP_ID) {
        // Same destinations as ProjectManagementShellTopNav (desktop bar), grouped the same
        // way the Auto Workshop drawer groups its sections.
        const pmSection = (sectionLabel: string, items: Array<{ to: string; label: string } | null>) => {
            const visible = items.filter((i): i is { to: string; label: string } => i != null);
            if (visible.length === 0) return null;
            return (
                <>
                    <Divider sx={{ my: 1 }} />
                    <ListSubheader
                        disableSticky
                        sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                    >
                        {sectionLabel}
                    </ListSubheader>
                    {visible.map(({ to, label }) => (
                        <ListItemButton key={to} component={Link} to={to} onClick={close} sx={itemSx}>
                            <ListItemText primary={label} />
                        </ListItemButton>
                    ))}
                </>
            );
        };

        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                {pmSection(translate('shell.project.projects', { _: 'Projects' }), [
                    canPmProjects ? { to: p('/projects'), label: translate('shell.project.projects', { _: 'Projects' }) } : null,
                    canPmTasks ? { to: p('/tasks'), label: translate('shell.project.tasks', { _: 'Tasks' }) } : null,
                    canPmWbs ? { to: p('/wbs'), label: translate('shell.project.wbs', { _: 'WBS' }) } : null,
                    canPmBudget ? { to: p('/budget'), label: translate('shell.project.budget', { _: 'Budget' }) } : null,
                    canPmSchedules ? { to: p('/projectPaymentSchedules'), label: translate('shell.project.payment_schedules', { _: 'Payment Schedules' }) } : null,
                    canPmChangeRequests ? { to: p('/projectChangeRequests'), label: translate('shell.project.change_requests', { _: 'Change Requests' }) } : null,
                ])}
                {pmSection(
                    translate('shell.project.reporting', { _: 'Reporting' }),
                    canPmProjects
                        ? (purchasesInstalled && canPurchaseRegister
                              ? [...PROJECT_REPORTS_NAV, PM_PURCHASE_REGISTER_NAV]
                              : PROJECT_REPORTS_NAV
                          ).map(({ resource, label }) => ({ to: p(`/${resource}`), label }))
                        : []
                )}
                {pmSection(translate('shell.project.configuration', { _: 'Configuration' }), [
                    canPmCategories ? { to: p('/projectCategories'), label: translate('shell.project.categories', { _: 'Project Categories' }) } : null,
                    canPmLaborTypes ? { to: p('/projectLaborTypes'), label: translate('shell.project.labor_types', { _: 'Labour Types' }) } : null,
                    canPmRoles ? { to: p('/projectRoles'), label: translate('shell.project.roles', { _: 'Project Roles' }) } : null,
                    canPmEmployees ? { to: p('/employee'), label: translate('shell.project.employees', { _: 'Employees' }) } : null,
                ])}
            </List>
        );
    } else if (activeAppId === PAYROLL_APP_ID) {
        // Same destinations as PayrollShellTopNav (desktop bar).
        const hrSection = (sectionLabel: string, items: Array<{ to: string; label: string } | null>) => {
            const visible = items.filter((i): i is { to: string; label: string } => i != null);
            if (visible.length === 0) return null;
            return (
                <>
                    <Divider sx={{ my: 1 }} />
                    <ListSubheader
                        disableSticky
                        sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                    >
                        {sectionLabel}
                    </ListSubheader>
                    {visible.map(({ to, label }) => (
                        <ListItemButton key={to} component={Link} to={to} onClick={close} sx={itemSx}>
                            <ListItemText primary={label} />
                        </ListItemButton>
                    ))}
                </>
            );
        };

        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                {hrSection(translate('shell.hr.payroll', { _: 'Payroll' }), [
                    canHrPayslips ? { to: p('/payslips'), label: translate('shell.hr.payslips', { _: 'Payslips' }) } : null,
                    canHrPeriods ? { to: p('/payrollPeriods'), label: translate('shell.hr.payroll_periods', { _: 'Payroll Periods' }) } : null,
                    canHrLoans ? { to: p('/loans'), label: translate('shell.hr.loans', { _: 'Loans & Advances' }) } : null,
                    canPmEmployees ? { to: p('/employee'), label: translate('shell.hr.employees', { _: 'Employees' }) } : null,
                ])}
                {hrSection(
                    translate('shell.hr.reporting', { _: 'Reporting' }),
                    canHrSalarySheet
                        ? PAYROLL_REPORTS_NAV.map(({ resource, label }) => ({ to: p(`/${resource}`), label }))
                        : []
                )}
                {hrSection(translate('shell.hr.configuration', { _: 'Configuration' }), [
                    canHrComponents ? { to: p('/salaryComponents'), label: translate('shell.hr.salary_components', { _: 'Salary Components' }) } : null,
                    canHrStructures ? { to: p('/salaryStructures'), label: translate('shell.hr.salary_structures', { _: 'Salary Structures' }) } : null,
                    canHrTaxSlabs ? { to: p('/taxSlabs'), label: translate('shell.hr.tax_slabs', { _: 'Income Tax Slabs' }) } : null,
                ])}
            </List>
        );
    } else if (activeAppId === DOCUMENT_MANAGEMENT_APP_ID) {
        // Same destinations as DocumentManagementShellTopNav (desktop bar).
        const dmsSection = (sectionLabel: string, items: Array<{ to: string; label: string } | null>) => {
            const visible = items.filter((i): i is { to: string; label: string } => i != null);
            if (visible.length === 0) return null;
            return (
                <>
                    <Divider sx={{ my: 1 }} />
                    <ListSubheader
                        disableSticky
                        sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px', color: 'primary.main' }}
                    >
                        {sectionLabel}
                    </ListSubheader>
                    {visible.map(({ to, label }) => (
                        <ListItemButton key={to} component={Link} to={to} onClick={close} sx={itemSx}>
                            <ListItemText primary={label} />
                        </ListItemButton>
                    ))}
                </>
            );
        };

        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                {dmsSection(translate('shell.documents.documents', { _: 'Documents' }), [
                    canDmsDocuments ? { to: p('/dmsDocuments'), label: translate('shell.documents.all_documents', { _: 'All Documents' }) } : null,
                ])}
                {dmsSection(translate('shell.documents.configuration', { _: 'Configuration' }), [
                    canDmsCategories ? { to: p('/dmsDocumentCategories'), label: translate('shell.documents.categories', { _: 'Document Categories' }) } : null,
                    canDmsTypes ? { to: p('/dmsDocumentTypes'), label: translate('shell.documents.types', { _: 'Document Types' }) } : null,
                ])}
                {dmsSection(translate('shell.documents.audit', { _: 'Audit & Tools' }), [
                    canDmsAccessLog ? { to: p('/dmsDocumentAccessLogs'), label: translate('shell.documents.access_log', { _: 'Access Log' }) } : null,
                    canDmsMigration ? { to: p('/migration'), label: translate('shell.documents.migration', { _: 'Migrate Legacy Documents' }) } : null,
                ])}
            </List>
        );
    } else if (activeAppId !== 'fbr-smart') {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
            </List>
        );
    } else {
        body = (
            <List dense disablePadding sx={{ py: 1 }}>
                <ListItemButton component={Link} to={root} onClick={close} sx={itemSx}>
                    <ListItemText primary={translate('ra.page.dashboard', { _: 'Dashboard' })} />
                </ListItemButton>
                <ListSubheader disableSticky sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px' }}>
                    {translate('pos.menu.sales', { _: 'Sales' })}
                </ListSubheader>
                <ListItemButton component={Link} to={p('/fbrInvoices')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.fbrInvoices.name', {
                            smart_count: 2,
                            _: 'FBR Invoices',
                        })}
                    />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/customers')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.customers.name', {
                            smart_count: 2,
                            _: 'Customers',
                        })}
                    />
                </ListItemButton>
                <ListSubheader disableSticky sx={{ fontWeight: 700, fontSize: 12, lineHeight: '32px' }}>
                    {translate('pos.menu.catalog', { _: 'Catalog' })}
                </ListSubheader>
                <ListItemButton component={Link} to={p('/productProfiles')} onClick={close} sx={itemSx}>
                    <ListItemText primary="Products" />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/fbrSalesTaxRates')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.fbrSalesTaxRates.name', {
                            smart_count: 2,
                            _: 'Taxes',
                        })}
                    />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/companies')} onClick={close} sx={itemSx}>
                    <ListItemText
                        primary={translate('resources.companies.name', {
                            smart_count: 1,
                            _: 'Company',
                        })}
                    />
                </ListItemButton>
                <ListItemButton component={Link} to={p('/fbrScenarios')} onClick={close} sx={itemSx}>
                    <ListItemText primary="FBR Scenarios" />
                </ListItemButton>
            </List>
        );
    }

    return (
        <>
            {/* Project Management's top nav is overflow-aware (OdooTopNavOverflowRow), so it stays
                usable from `md` up and the hamburger is only needed on phones/small tablets.
                Every other app still swaps to the drawer below `lg`. */}
            <Box
                sx={{
                    display:
                        activeAppId === PROJECT_MANAGEMENT_APP_ID
                            ? { xs: 'flex', md: 'none' }
                            : { xs: 'flex', lg: 'none' },
                    alignItems: 'center',
                }}
            >
                <IconButton
                    color="inherit"
                    edge="start"
                    aria-label={translate('ra.action.open_navigation', { _: 'Open menu' })}
                    onClick={() => setOpen(true)}
                    size="small"
                >
                    <MenuIcon />
                </IconButton>
            </Box>
            <Drawer
                anchor="left"
                open={open}
                onClose={close}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {translate('pos.odoo.menu', { _: 'Menu' })}
                    </Typography>
                </Box>
                <Box role="navigation">
                    {body}
                </Box>
            </Drawer>
        </>
    );
}

export default OdooShellMobileNav;
